"use client";

import type { FC, ReactNode } from "react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/20/solid";
import * as RadixSelect from "@radix-ui/react-select";
import { cn } from "@/lib/cn";
import { FieldLabel, FieldMessage, SELECT_PANEL_WIDTH, type FieldState } from "./field-chrome";

type SelectOption = {
  value: string;
  label: string;
};

type SelectProps = Omit<RadixSelect.SelectProps, "children" | "required"> & {
  className?: string;
  label?: ReactNode;
  message?: ReactNode;
  state?: FieldState;
  required?: boolean;
  placeholder?: string;
  options: SelectOption[];
  id?: string;
};

export const Select: FC<SelectProps> = ({
  className,
  label,
  message,
  state = "default",
  required,
  placeholder = "Please input",
  options,
  id,
  ...props
}) => (
  <div className="flex w-full flex-col gap-8">
    <FieldLabel htmlFor={id} state={state} required={required}>
      {label}
    </FieldLabel>
    <RadixSelect.Root required={required} {...props}>
      <RadixSelect.Trigger
        id={id}
        className={cn(
          "flex w-full cursor-pointer items-center justify-between gap-8 rounded-medium border bg-neutral-white p-16 text-16 text-neutral-black outline-none focus:border-brand-blue disabled:cursor-not-allowed disabled:bg-neutral-black/5 data-[placeholder]:text-neutral-black/35",
          state === "error" ? "border-brand-red" : "border-neutral-mid-grey",
          className,
        )}
      >
        <span className="min-w-0 truncate">
          <RadixSelect.Value placeholder={placeholder} />
        </span>
        <RadixSelect.Icon>
          <ChevronDownIcon className="size-24 shrink-0 text-neutral-black" />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <RadixSelect.Content
          position="popper"
          side="bottom"
          sideOffset={8}
          collisionPadding={16}
          className={cn(
            "z-50 overflow-hidden rounded-small border border-neutral-light-grey bg-neutral-white shadow-lg data-[state=closed]:animate-[dropdown-out_150ms_ease-in] data-[state=open]:animate-[dropdown-in_150ms_ease-out]",
            SELECT_PANEL_WIDTH,
          )}
        >
          {/* Same 280px cap as `Combobox`, so a long taxonomy list scrolls in the panel instead of
              running off the bottom of the screen. */}
          <RadixSelect.Viewport className="max-h-280 overflow-y-auto p-4">
            {options.map((option) => (
              <RadixSelect.Item
                key={option.value}
                value={option.value}
                className="flex cursor-pointer items-center gap-6 rounded-small px-16 py-12 text-16 text-neutral-black outline-none transition-colors duration-100 data-highlighted:bg-tone-aqua-tint"
              >
                <span className="flex size-20 shrink-0 items-center justify-center">
                  <RadixSelect.ItemIndicator>
                    <CheckIcon className="size-20 text-neutral-black" />
                  </RadixSelect.ItemIndicator>
                </span>
                <RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
    <FieldMessage state={state}>{message}</FieldMessage>
  </div>
);
